<template>
  <div class="crew-detail-container">
    <!-- 船員基本情報カード -->
    <CrewBasicInfoCard
      v-if="crewBasicInfo"
      :name="crewBasicInfo.name"
      :nameKana="crewBasicInfo.name_kana"
      :isEnrolled="isEnrolled"
      :appointedRole="appointedRoleForCard"
      :employmentStatus="employmentStatusForCard"
      :companyName="companyNameForCard"
      :employeeNumber="crewBasicInfo.employee_number"
    />

    <div
      v-if="selectedCrewDetailViewMode === 'FullDisplay'"
      class="vx-card mt-4 overflow-hidden"
    >
      <component
        :is="licenseAddComponent"
        :crewId="crewId"
        :category="currentLicenseCategory"
        :sub-category="currentLicenseSubCategory"
        :license="currentLicense"
      />
    </div>
    <div v-else>
      <!-- メニューとコンテンツエリア -->
      <div class="vx-card mt-4 overflow-hidden">
        <div class="flex">
          <!-- サイドメニュー -->
          <div
            class="flex-shrink-0"
            style="width: 200px; border-right: 1px solid #E0E0E0;"
          >
            <SubMenu
              :menuItems="menuItems"
              :activeName="$route.name"
              :activeParams="$route.params"
              :activeQueries="$route.query"
              :isEditing="isEditing"
              @item-click="handleItemClick"
            />
          </div>

          <!-- コンテンツエリア -->
          <div class="flex-1">
            <!-- ルーティングまたはメタデータによってコンテンツを切り替え -->
            <!-- メタデータにuseRouterViewがtrueの場合はrouter-viewを使用 -->
            <router-view v-if="isRouteView" :key="$route.fullPath" />

            <!-- 条件分岐でコンテンツを表示（メタデータのcontentTypeまたはルート名から判定） -->
            <div v-else>
              <!-- 船員基本情報 -->
              <component
                v-if="
                  selectedSubMenu === 'CrewBasicInfo' && isCrewBasicInfoLoaded
                "
                :is="infoComponent"
                :crewBasicInfo="crewBasicInfo"
                :crewRoles="crewRoles"
                @refresh="refreshCrewBasicInfo"
                @edit-mode-change="handleEditModeChange"
              />
              <div
                v-else-if="
                  selectedSubMenu === 'CrewBasicInfo' && !isCrewBasicInfoLoaded
                "
                class="m-6"
              >
                読み込み中
              </div>

              <!-- 保有資格一覧 -->
              <component
                v-else-if="selectedSubMenu === 'Dashboard'"
                :is="dashboardComponent"
                :crewId="crewId"
                @edit-mode-change="handleEditModeChange"
              />

              <!-- 保有資格詳細（動的コンポーネント） -->
              <component
                v-else-if="
                  this.currentLicenseCategoryInfo
                    ? this.currentLicenseCategoryInfo.category ===
                      selectedSubMenu
                    : false
                "
                :is="licenseViewComponent"
                :crewId="crewId"
                :category="this.currentLicenseCategoryInfo.category"
                @edit-mode-change="handleEditModeChange"
              />

              <!-- 配乗基本情報 -->
              <component
                v-else-if="selectedSubMenu === 'AssignmentBasicInfo'"
                :is="assignmentComponent"
                :crewId="crewId"
                @edit-mode-change="handleEditModeChange"
              />

              <!-- その他のコンテンツ（メタデータで指定） -->
              <component
                v-else-if="currentContentComponent"
                :is="currentContentComponent"
                :crewId="crewId"
              />

              <!-- デフォルト: ルート名に基づくコンテンツ -->
              <div v-else>
                <p>コンテンツが見つかりません</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CrewBasicInfoCard from "../components/CrewBasicInfoCard.vue";
import SubMenu from "../components/SubMenu.vue";
import { EnterpriseCrewsService } from "../service/CrewBasicInfoService.js";
import { EnterpriseCrewRolesService } from "../service/CrewBasicInfoService.js";
import moment from "moment";
import { ColorMixin } from "@/mixins";
import { mapGetters } from "vuex";

export default {
  name: "CrewDetail",
  components: {
    CrewBasicInfoCard,
    SubMenu,
  },
  mixins: [ColorMixin],
  data() {
    return {
      crewBasicInfo: null,
      isCrewBasicInfoLoaded: false,

      crewRoles: [], // 役職マスター
      isEditing: false, // 編集中フラグ
    };
  },
  computed: {
    ...mapGetters(["addonMap"]),
    ...mapGetters("crewSettingsLicense", ["getHavingLicenseCategories"]),
    crewId() {
      return this.$route.params.id;
    },
    /**
     * アドオン 32 (Crew_Assignment) があるかどうか
     */
    hasCrewAssignment() {
      // addonMap で ID "32" をチェック
      return !!this.addonMap["32"];
    },
    /**
     * フィルタリングされたメニューアイテム
     */
    menuItems() {
      const items = [
        {
          label: "船員基本情報",
          icon: "person",
          name: "enterprise-management-crew-settings-info",
          params: { id: this.$route.params.id },
        },
        {
          label: "保有資格",
          icon: "fact_check",
          name: "enterprise-management-crew-settings-license",
          submenu: [
            {
              label: "ダッシュボード",
              name: "enterprise-management-crew-settings-license-dashboard",
              params: { id: this.$route.params.id },
            },
            // 動的に大区分のメニューアイテムを追加
            ...this.getHavingLicenseCategories.map(category => ({
              label: category.label,
              name: "enterprise-management-crew-settings-license",
              params: { id: this.$route.params.id },
              query: { category: category.category },
            })),
          ],
        },
      ];

      // アドオン 32 がある場合のみ配乗基本情報メニューを追加
      if (this.hasCrewAssignment) {
        items.push({
          label: "配乗管理情報",
          icon: "directions_boat",
          name: "enterprise-management-crew-settings-assignment",
          params: { id: this.$route.params.id },
        });
      }

      return items;
    },
    /**
     * 在籍フラグ
     * retirement_date が null/未設定なら在籍中
     * retirement_date が存在し、現在日時より前なら退職済
     * それ以外（今日以降）は在籍中
     */
    isEnrolled() {
      if (!this.crewBasicInfo) {
        return false;
      }

      const retirementDateStr = this.crewBasicInfo.retirement_date;
      if (!retirementDateStr) {
        // 退職日が未設定の場合は在籍中
        return true;
      }

      const retirementDate = moment(retirementDateStr);
      if (retirementDate.isValid() && retirementDate.isBefore(moment())) {
        return false;
      }
      return true;
    },
    /**
     * カード表示用の最新所属企業レコード（start_dateが最も新しいもの）
     */
    latestAffiliatedCompany() {
      if (
        !this.crewBasicInfo ||
        !this.crewBasicInfo.affiliated_companies ||
        !Array.isArray(this.crewBasicInfo.affiliated_companies) ||
        this.crewBasicInfo.affiliated_companies.length === 0
      ) {
        return null;
      }

      const items = this.crewBasicInfo.affiliated_companies.filter(
        item => !!item
      );
      if (items.length === 0) return null;

      // start_dateの新しい順にソートして先頭を採用
      const sorted = [...items].sort((a, b) => {
        const aDate = a.start_date ? new Date(a.start_date) : null;
        const bDate = b.start_date ? new Date(b.start_date) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate - aDate;
      });

      return sorted[0];
    },
    /**
     * カード表示用の最新所属企業名
     */
    companyNameForCard() {
      return this.latestAffiliatedCompany && this.latestAffiliatedCompany.name
        ? this.latestAffiliatedCompany.name
        : "";
    },
    /**
     * カード表示用の最新社員区分コード
     */
    employmentStatusForCard() {
      return this.latestAffiliatedCompany && this.latestAffiliatedCompany.status
        ? this.latestAffiliatedCompany.status
        : "";
    },
    /**
     * カード表示用の最新辞令職ラベル
     */
    appointedRoleForCard() {
      if (
        !this.crewBasicInfo ||
        !this.crewBasicInfo.available_roles ||
        !Array.isArray(this.crewBasicInfo.available_roles) ||
        this.crewBasicInfo.available_roles.length === 0
      ) {
        return "";
      }

      const items = this.crewBasicInfo.available_roles.filter(
        item => item && item.start_date
      );
      if (items.length === 0) return "";

      const sorted = [...items].sort((a, b) => {
        const aDate = a.start_date ? new Date(a.start_date) : null;
        const bDate = b.start_date ? new Date(b.start_date) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate - aDate;
      });

      const latest = sorted[0];
      if (!latest || !latest.role_id) return "";

      const role = this.crewRoles.find(
        r => r.id === latest.role_id || String(r.id) === String(latest.role_id)
      );
      return role ? role.label : "";
    },
    /**
     * router-viewを使うかどうか
     * メタデータにuseRouterViewがtrueの場合はrouter-viewを使用
     */
    isRouteView() {
      return this.$route.meta && this.$route.meta.useRouterView === true;
    },
    /**
     * 現在のコンテンツタイプ
     * ルート名またはメタデータから判定
     */
    selectedSubMenu() {
      // メタデータのselecteeSubMenuから判定
      if (this.$route.meta && this.$route.meta.selectedSubMenu) {
        return this.$route.meta.selectedSubMenu;
      }

      // もしメタデータのselecteeSubMenuがない場合は、ルートのクエリパラメーターから判定
      if (this.$route.query && this.$route.query.category) {
        return this.$route.query.category;
      }

      return null;
    },
    /**
     * 現在のビュータイプ
     * ルート名またはメタデータから判定
     */
    selectedCrewDetailViewMode() {
      // メタデータのcrewDetailViewModeから判定
      if (this.$route.meta && this.$route.meta.crewDetailViewMode) {
        return this.$route.meta.crewDetailViewMode;
      }

      return null;
    },
    /**
     * 現在の大区分（クエリパラメータから取得）
     */
    currentLicenseCategory() {
      return this.$route.query.category || null;
    },
    /**
     * 現在の小区分（クエリパラメータから取得）
     */
    currentLicenseSubCategory() {
      return this.$route.query.subCategory || null;
    },
    /**
     * 現在の資格（クエリパラメータから取得）
     */
    currentLicense() {
      return this.$route.query.license || null;
    },
    /**
     * 現在の大区分（クエリパラメータから取得）からカテゴリ情報を取得
     */
    currentLicenseCategoryInfo() {
      if (!this.currentLicenseCategory) {
        return null;
      }
      return this.getHavingLicenseCategories.find(
        category => category.category === this.currentLicenseCategory
      );
    },
    /**
     * メタデータで指定されたコンテンツコンポーネント
     */
    currentContentComponent() {
      if (this.$route.meta && this.$route.meta.contentComponent) {
        return this.$route.meta.contentComponent;
      }
      return null;
    },
    /**
     * 船員基本情報コンポーネント
     */
    infoComponent() {
      // メタデータで指定されている場合
      if (this.$route.meta && this.$route.meta.infoComponent) {
        return this.$route.meta.infoComponent;
      }
      // デフォルトコンポーネント（必要に応じて動的インポート）
      return () => import("../crew-basic-info/CrewBasicInfo.vue");
    },
    /**
     * 保有資格ダッシュボードコンポーネント
     */
    dashboardComponent() {
      // メタデータで指定されている場合
      if (this.$route.meta && this.$route.meta.dashboardComponent) {
        return this.$route.meta.dashboardComponent;
      }
      // デフォルトコンポーネント（必要に応じて動的インポート）
      return () => import("../license/license-dashboard/LicenseDashboard.vue");
    },
    /**
     * 保有資格新規作成フォームコンポーネント
     */
    licenseAddComponent() {
      // メタデータで指定されている場合
      if (this.$route.meta && this.$route.meta.licenseAddComponent) {
        return this.$route.meta.licenseAddComponent;
      }
      // デフォルトコンポーネント（必要に応じて動的インポート）
      return () => import("../license/license-form/LicenseForm.vue");
    },
    /**
     * 保有資格フォームコンポーネント（動的）
     */
    licenseViewComponent() {
      // クエリパラメーターにカテゴリがない場合は null を返す
      if (!this.currentLicenseCategory) {
        return null;
      }
      // デフォルトコンポーネント
      return () => import("../license/license-list/LicenseList.vue");
    },
    /**
     * 配乗基本情報コンポーネント
     */
    assignmentComponent() {
      // メタデータで指定されている場合
      if (this.$route.meta && this.$route.meta.assignmentComponent) {
        return this.$route.meta.assignmentComponent;
      }
      // デフォルトコンポーネント（必要に応じて動的インポート）
      return () => import("../assignment/AssignmentBasicInfo.vue");
    },
  },
  watch: {
    "$route.params.id"() {
      this.fetchCrewBasicInfo();
      // 船員IDが変わった場合は編集中フラグをリセット
      this.isEditing = false;
    },
    "$route.name"() {
      // ルートが変わった場合は編集中フラグをリセット
      this.isEditing = false;
    },
    // "$route.query.category": {
    //   immediate: true,
    //   async handler() {
    //     // クエリパラメータが変わった場合は編集中フラグをリセット
    //     this.isEditing = false;

    //     if (!this.currentLicenseCategory) {
    //       // Category が無ければ終了
    //       return;
    //     }

    //     // licenseCategories が未ロードならロードしてからパンくず更新
    //     if (!this.licenseCategories || this.licenseCategories.length === 0) {
    //       await this.fetchLicenseCategories();
    //     }

    //     this.updateBreadcrumb();
    //   },
    // },
  },
  created() {
    this.fetchCrewBasicInfo();
    this.fetchCrewRoles();
    this.fetchCrewHavingLicenses();
  },
  methods: {
    async fetchCrewRoles() {
      const crewRoles = await EnterpriseCrewRolesService.fetch();
      this.crewRoles = crewRoles;
    },
    async fetchCrewHavingLicenses() {
      try {
        const crewId = this.$route.params.id;
        if (!crewId) return;

        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });

        this.$store.dispatch("crewSettingsLicense/fetchAllLicenseData", {
          enterpriseCrewId: this.$route.params.id,
        });
      } catch (error) {
        console.error("Failed to fetch crew having licenses:", error);
        this.$vs.notify({
          title: "エラー",
          text: "保有資格情報の取得に失敗しました",
          color: "danger",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async refreshCrewBasicInfo() {
      this.fetchCrewBasicInfo();
    },

    async fetchCrewBasicInfo() {
      try {
        const crewId = this.$route.params.id;
        if (!crewId) return;

        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        this.crewBasicInfo = await EnterpriseCrewsService.fetchById(crewId);
        this.isCrewBasicInfoLoaded = true;
      } catch (error) {
        console.error("Failed to fetch crew basic info:", error);
        this.$vs.notify({
          title: "エラー",
          text: "船員情報の取得に失敗しました",
          color: "danger",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    handleItemClick(item) {
      // メニューアイテムクリック時の処理
      // SubMenuコンポーネントで既にルーティングが行われるため、
      // ここでは追加の処理が必要な場合のみ実装
    },
    /**
     * 編集モードの変更をハンドル
     */
    handleEditModeChange(isEditing) {
      this.isEditing = isEditing;
    },
    /**
     * 動的パンクズ更新
     */
    // updateBreadcrumb() {
    //   const category = this.licenseCategories.find(
    //     c => c.value === this.currentLicenseCategory
    //   );

    //   const breadcrumb = [
    //     { title: "船員設定", color: "#626262" },
    //     {
    //       title: "船員一覧",
    //       color: "#2D9CDB",
    //       url: { name: "enterprise-management-crew-settings-list" },
    //     },
    //     { title: "保有資格", color: "#626262" },
    //     {
    //       title: category ? category.label : "",
    //       active: true,
    //     },
    //   ];

    //   this.$route.meta.breadcrumb = breadcrumb;
    // },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";
.crew-detail-container {
  width: 100%;
  font-family: $aisea-font-family;
}
</style>
